import React, { useState } from 'react';
import { FiPlus, FiEdit2, FiTrash2, FiSearch, FiTruck } from 'react-icons/fi';
import useEntity from '../hooks/useEntity';
import Modal from '../components/ui/Modal';
import { useAuth } from '../contexts/AuthContext';
import { featureFlagEnabled } from '../config/modulos';
import { puede } from '../utils/permisos';

const VACIO = {
  matricula: '', tipoVehiculo: 'turismo', tipoInspeccion: 'periodica',
  clienteNombre: '', clienteTelefono: '', clienteEmail: '', fecha: '', hora: '', estado: 'reservada', notas: '',
};
const TIPOS_V = ['turismo', 'moto', 'furgoneta', 'camion', 'remolque', 'otro'];
const TIPOS_I = ['periodica', 'previa_matriculacion', 'reforma', 'segunda'];
const ESTADOS = { reservada: 'Reservada', confirmada: 'Confirmada', realizada: 'Realizada', no_presentado: 'No presentado', cancelada: 'Cancelada' };
const BADGE = { reservada: 'badge-info', confirmada: 'badge-ok', realizada: 'badge', no_presentado: 'badge-warning', cancelada: 'badge-danger' };

export default function CitasITV() {
  const { usuario, empresa } = useAuth();
  const featureActiva = usuario?.rol === 'superadmin' || featureFlagEnabled(empresa, 'itv_adelantador');
  const { items, cargando, error, crear, actualizar, eliminar } = useEntity('citas-itv');
  const [modal, setModal] = useState(false);
  const [editando, setEditando] = useState(null);
  const [form, setForm] = useState(VACIO);
  const [busqueda, setBusqueda] = useState('');
  const [guardando, setGuardando] = useState(false);

  const puedeCrear = puede(usuario, 'citas_itv', 'crear');
  const puedeEditar = puede(usuario, 'citas_itv', 'editar');
  const puedeEliminar = puede(usuario, 'citas_itv', 'eliminar');

  if (!featureActiva) {
    return (
      <div className="page">
        <h1 className="page-title">Citas ITV</h1>
        <div className="alert-warning">La funcionalidad de ITV no está habilitada para esta empresa.</div>
      </div>
    );
  }

  const abrirNuevo = () => { setEditando(null); setForm(VACIO); setModal(true); };
  const abrirEditar = (c) => { setEditando(c); setForm({ ...VACIO, ...c, fecha: c.fecha ? c.fecha.slice(0, 10) : '' }); setModal(true); };

  const set = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const onGuardar = async (e) => {
    e.preventDefault();
    setGuardando(true);
    try {
      if (editando) await actualizar(editando._id, form);
      else await crear(form);
      setModal(false);
    } catch (err) { alert(err.response?.data?.error || 'Error al guardar'); }
    finally { setGuardando(false); }
  };

  const onEliminar = async (c) => {
    if (!window.confirm(`¿Eliminar la cita ITV de "${c.matricula}"?`)) return;
    try { await eliminar(c._id); } catch { alert('No se pudo eliminar'); }
  };

  const fmt = (d) => (d ? new Date(d).toLocaleDateString('es-ES') : '—');
  const filtrados = items.filter((c) => [c.matricula, c.clienteNombre].join(' ').toLowerCase().includes(busqueda.toLowerCase()));

  return (
    <div className="page">
      <div className="page-head">
        <div><h1 className="page-title">Citas ITV</h1><p className="page-subtitle">{items.length} cita(s) de inspección</p></div>
        <div className="head-actions">
          {featureActiva && (
            <a className="btn btn-secondary" href="/app/adelantador-itv">
              <FiTruck /> Adelantador de cita ITV
            </a>
          )}
          {puedeCrear && <button className="btn btn-primary" onClick={abrirNuevo}><FiPlus /> Nueva cita ITV</button>}
        </div>
      </div>

      <div className="toolbar">
        <div className="search-box"><FiSearch /><input placeholder="Buscar por matrícula o cliente" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} /></div>
      </div>

      {error && <div className="alert-error">{error}</div>}
      {cargando ? <p className="empty">Cargando…</p> : filtrados.length === 0 ? <p className="empty"><FiTruck /> No hay citas ITV.</p> : (
        <div className="table-wrap">
          <table className="data-table">
            <thead><tr><th>Matrícula</th><th>Cliente</th><th>Vehículo</th><th>Inspección</th><th>Fecha</th><th>Hora</th><th>Estado</th><th></th></tr></thead>
            <tbody>
              {filtrados.map((c) => (
                <tr key={c._id}>
                  <td data-label="Matrícula"><strong>{c.matricula}</strong></td>
                  <td data-label="Cliente">{c.clienteNombre}</td>
                  <td data-label="Vehículo">{c.tipoVehiculo}</td>
                  <td data-label="Inspección">{c.tipoInspeccion}</td>
                  <td data-label="Fecha">{fmt(c.fecha)}</td>
                  <td data-label="Hora">{c.hora}</td>
                  <td data-label="Estado"><span className={`badge ${BADGE[c.estado]}`}>{ESTADOS[c.estado]}</span></td>
                  <td className="acciones">
                    {puedeEditar && <button className="icon-btn" onClick={() => abrirEditar(c)}><FiEdit2 /></button>}
                    {puedeEliminar && <button className="icon-btn danger" onClick={() => onEliminar(c)}><FiTrash2 /></button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal abierto={modal} onCerrar={() => setModal(false)} titulo={editando ? 'Editar cita ITV' : 'Nueva cita ITV'}>
        <form onSubmit={onGuardar} className="form-grid">
          <label className="field"><span>Matrícula *</span><input required value={form.matricula} onChange={set('matricula')} /></label>
          <label className="field"><span>Tipo de vehículo</span><select value={form.tipoVehiculo} onChange={set('tipoVehiculo')}>{TIPOS_V.map((t) => <option key={t} value={t}>{t}</option>)}</select></label>
          <label className="field span-2"><span>Tipo de inspección</span><select value={form.tipoInspeccion} onChange={set('tipoInspeccion')}>{TIPOS_I.map((t) => <option key={t} value={t}>{t}</option>)}</select></label>
          <label className="field span-2"><span>Cliente *</span><input required value={form.clienteNombre} onChange={set('clienteNombre')} /></label>
          <label className="field"><span>Teléfono</span><input value={form.clienteTelefono} onChange={set('clienteTelefono')} /></label>
          <label className="field"><span>Email</span><input type="email" value={form.clienteEmail} onChange={set('clienteEmail')} /></label>
          <label className="field"><span>Fecha *</span><input type="date" required value={form.fecha} onChange={set('fecha')} /></label>
          <label className="field"><span>Hora *</span><input type="time" required value={form.hora} onChange={set('hora')} /></label>
          <label className="field span-2"><span>Estado</span><select value={form.estado} onChange={set('estado')}>{Object.entries(ESTADOS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select></label>
          <label className="field span-2"><span>Notas</span><textarea rows={2} value={form.notas} onChange={set('notas')} /></label>
          <div className="form-actions span-2">
            <button type="button" className="btn" onClick={() => setModal(false)}>Cancelar</button>
            <button type="submit" className="btn btn-primary" disabled={guardando}>{guardando ? 'Guardando…' : 'Guardar'}</button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
